import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useCreateContract, useUpdateContract } from "@/lib/api/otc";
import {
  transformToApiContract,
  isFormValid,
} from "@/section/otc/ContractForm";
import { NewContractInfo, UpdateContractInfo } from "@/section/otc/OtcBearState";

interface UseOtcContractFormHandlersProps {
  refetchContracts: () => void;
  onCloseModal: () => void;
  updateAttemptedSave: (status: boolean) => void;
}

export function useOtcContractFormHandlers({
  refetchContracts,
  onCloseModal,
  updateAttemptedSave,
}: UseOtcContractFormHandlersProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const createContract = useCreateContract();
  const updateContract = useUpdateContract();

  const onSubmitForm = async (
    formData: NewContractInfo | UpdateContractInfo,
    isUpdateForm: boolean,
  ): Promise<boolean> => {
    updateAttemptedSave(true);
    // NOTE: we convert it json so it convert to how the api expects it.
    // i.e. decimals become strings
    if (!isFormValid(isUpdateForm, formData)) {
      return false;
    }

    if (isSaving) return false;

    setIsSaving(true);

    const data = JSON.parse(JSON.stringify(transformToApiContract(formData)));
    const saveResult = await (!!isUpdateForm
      ? updateContract(data as any)
      : createContract(data as any));

    updateAttemptedSave(false);
    setIsSaving(false);

    if (!saveResult.success) {
      toast({
        title: isUpdateForm
          ? "Failed to update contract"
          : "Failed to create contract",
        description: "",
      });
    } else {
      onCloseModal();
      refetchContracts();
      toast({
        title: isUpdateForm ? "Updated" : "Created",
        description: "",
      });
    }

    return true;
  };

  return {
    onSubmitForm,
    isSaving,
    createContract,
    updateContract,
  };
}

